import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  type BankAccountListBankAccountsData,
  type BankAccountWithDetails,
  bankAccountDeleteBankAccount,
  bankAccountListBankAccountsOptions,
} from '@column/api';
import { Button, Dropdown, Icon, Table, TableColumn, TableVariant, Tooltip, formatNumber } from '@column/column-ui-kit';

import { FilterDropdown } from '~/app/pages/Transfers/Overview';
import { ROUTE } from '~/app/routes';
import { EmptyState, PageHeader, PopoverFilterTransferDateData, PopoverFilterWrapper } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { Search } from '~/components/Search';
import { AbsoluteTime, RelativeTime } from '~/elements';
import { useDebounce } from '~/hooks/useDebounce';
import { useQueryParams } from '~/hooks/useQueryParams';
import { usePinnedBankAccounts } from '~/lib/api/bankAccounts';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner } from '~/styles';

import { BankAccountFiltersModal } from './_components/BankAccountFiltersModal';
import { ButtonPin } from './_components/ButtonPin';
import { PopoverFilterBalance, PopoverFilterBalanceData } from './_components/PopoverFilterBalance';
import { PopoverFilterCreatedDate } from './_components/PopoverFilterCreatedDate';

type SortSelection = '' | 'created_at:desc' | 'created_at:asc' | 'balance:asc' | 'balance:desc';
type BankAccountType = '' | 'CHECKING' | 'OVERDRAFT_RESERVE' | 'PROGRAM_RESERVE' | 'UNKNOWN';
type OverdraftableSelection = '' | 'true' | 'false';

type BankAccountListQuery = NonNullable<BankAccountListBankAccountsData['query']>;
type BankAccountRow = Omit<BankAccountWithDetails, 'created_at'> & { created_at: Date };

interface BankAccountFilters {
  balance: PopoverFilterBalanceData;
  sort: SortSelection;
  type: BankAccountType;
  overdraftable: OverdraftableSelection;
  query: string;
  date: Partial<PopoverFilterTransferDateData>;
}

const DEFAULT_SORT: SortSelection = 'created_at:desc';

const EMPTY_FILTERS: BankAccountFilters = {
  balance: {},
  sort: '',
  type: '',
  overdraftable: '',
  query: '',
  date: {},
};

// Mirrors BankAccountFilters but as flat string-keyed params so the filter set
// can be round-tripped through the URL via useQueryParams.
interface BankAccountFilterUrlParams {
  type?: string;
  overdraftable?: string;
  query?: string;
  sort?: string;
  'balance.gte'?: string;
  'balance.lte'?: string;
  'created.gte'?: string;
  'created.lt'?: string;
  starting_after?: string;
  page_index?: string;
}

const isBankAccountType = (value: string): value is BankAccountType =>
  value === '' ||
  value === 'CHECKING' ||
  value === 'OVERDRAFT_RESERVE' ||
  value === 'PROGRAM_RESERVE' ||
  value === 'UNKNOWN';

const isOverdraftableSelection = (value: string): value is OverdraftableSelection =>
  value === '' || value === 'true' || value === 'false';

const isSortSelection = (value: string): value is SortSelection =>
  value === '' ||
  value === 'created_at:desc' ||
  value === 'created_at:asc' ||
  value === 'balance:asc' ||
  value === 'balance:desc';

const toNumber = (value: string | undefined): number | undefined => {
  if (value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const filtersFromUrl = (params: BankAccountFilterUrlParams | undefined): BankAccountFilters => {
  if (!params) return EMPTY_FILTERS;

  const type = params.type && isBankAccountType(params.type) ? params.type : '';
  const overdraftable =
    params.overdraftable && isOverdraftableSelection(params.overdraftable) ? params.overdraftable : '';
  const sort = params.sort && isSortSelection(params.sort) ? params.sort : '';

  return {
    balance: {
      balanceGte: toNumber(params['balance.gte']),
      balanceLte: toNumber(params['balance.lte']),
    },
    sort,
    type,
    overdraftable,
    query: params.query ?? '',
    date: {
      createdAtGte: params['created.gte'],
      createdAtLt: params['created.lt'],
    },
  };
};

const filtersToUrl = (filters: BankAccountFilters): BankAccountFilterUrlParams => {
  const params: BankAccountFilterUrlParams = {};
  if (filters.type) params.type = filters.type;
  if (filters.overdraftable) params.overdraftable = filters.overdraftable;
  if (filters.query) params.query = filters.query;
  if (filters.sort) params.sort = filters.sort;
  if (filters.balance.balanceGte !== undefined && filters.balance.balanceGte > 0) {
    params['balance.gte'] = String(filters.balance.balanceGte);
  }
  if (filters.balance.balanceLte !== undefined && filters.balance.balanceLte > 0) {
    params['balance.lte'] = String(filters.balance.balanceLte);
  }
  if (filters.date.createdAtGte) params['created.gte'] = filters.date.createdAtGte;
  if (filters.date.createdAtLt) params['created.lt'] = filters.date.createdAtLt;
  return params;
};

interface OptionalDateRangeInput {
  startDateString?: string;
  endDateString?: string;
}

// `created.lt` on the backend is a strict upper bound, so map the end date to
// the start of the following day to include the entire selected end date.
const startOfNextDay = (dateString: string): string | undefined => {
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + 1);
  return date.toISOString();
};

const startOfDay = (dateString: string): string | undefined => {
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

export const convertOptionalDatesToRange = ({
  startDateString,
  endDateString,
}: OptionalDateRangeInput): Partial<PopoverFilterTransferDateData> => {
  const createdAtRange: Partial<PopoverFilterTransferDateData> = {};

  if (startDateString) {
    createdAtRange.createdAtGte = startOfDay(startDateString);
  }

  if (endDateString) {
    createdAtRange.createdAtLt = startOfNextDay(endDateString);
  }

  return createdAtRange;
};

const SORT_OPTIONS: { label: string; value: SortSelection }[] = [
  { label: 'Created (newest to oldest)', value: 'created_at:desc' },
  { label: 'Created (oldest to newest)', value: 'created_at:asc' },
  { label: 'Balance (low to high)', value: 'balance:asc' },
  { label: 'Balance (high to low)', value: 'balance:desc' },
];

const TYPE_OPTIONS: { label: string; value: BankAccountType }[] = [
  { label: 'Checking', value: 'CHECKING' },
  { label: 'Overdraft Reserve', value: 'OVERDRAFT_RESERVE' },
  { label: 'Program Reserve', value: 'PROGRAM_RESERVE' },
  { label: 'Unknown', value: 'UNKNOWN' },
];

const OVERDRAFTABLE_OPTIONS: { label: string; value: OverdraftableSelection }[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const OVERDRAFTABLE_SELECTED_LABELS: Record<OverdraftableSelection, string> = {
  '': 'Overdraftable',
  true: 'Overdraftable',
  false: 'Not overdraftable',
};

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 var(--page-margin) 12px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const CompactFilterDropdown = styled(FilterDropdown)`
  min-width: 0;
`;

const AccountSearch = styled(Search)`
  max-width: 100%;
  transition: width 0.2s;

  label {
    flex-grow: 1;
    min-width: 0;
  }

  input {
    min-width: 0;
  }
`;

const BalanceAmount = styled.div`
  font-size: 13px;
  line-height: 24px;
  white-space: nowrap;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  strong {
    font-weight: 500;
    color: ${({ theme }) => theme.secondary.background};
  }
`;

const NamePrimary = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
`;

const NameNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
`;

const OverdraftableIcon = styled(Tooltip)`
  display: inline-block;
  width: 16px;
  height: 16px;
  vertical-align: top;

  svg {
    --icon-size: 16px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledDropdown = styled(Dropdown)`
  margin: -10px 0;
`;

const CreatedTimeCell = styled(Table.Cell)`
  position: relative;
  overflow: visible;

  &:hover {
    z-index: 50;
  }

  & > div {
    overflow: visible;
  }

  & > div > div:last-child {
    z-index: 60;
  }
`;

const buildQuery = (
  filters: BankAccountFilters,
  balanceEnabled: boolean,
  pageSize: number,
  cursor?: string
): BankAccountListQuery => {
  const query: BankAccountListQuery = {
    limit: pageSize,
  };

  if (cursor) query.starting_after = cursor;
  // With the search flag on, use the full-text `query` param. With it off, the
  // input falls back to the legacy description substring filter.
  if (filters.query) {
    if (balanceEnabled) {
      query.query = filters.query;
    } else {
      query.description = filters.query;
    }
  }
  if (filters.type) query.type = filters.type;
  if (filters.overdraftable) query.is_overdraftable = filters.overdraftable === 'true';
  if (filters.date.createdAtGte) query['created.gte'] = filters.date.createdAtGte;
  if (filters.date.createdAtLt) query['created.lt'] = filters.date.createdAtLt;

  if (balanceEnabled) {
    if (filters.balance.balanceGte !== undefined && filters.balance.balanceGte > 0) {
      query['balance.gte'] = filters.balance.balanceGte;
    }
    if (filters.balance.balanceLte !== undefined && filters.balance.balanceLte > 0) {
      query['balance.lte'] = filters.balance.balanceLte;
    }
    const effectiveSort = filters.sort || (filters.query ? '' : DEFAULT_SORT);
    if (effectiveSort) {
      const [sort, sortOrder] = effectiveSort.split(':');
      query.sort = sort;
      query.sort_order = sortOrder;
    }
  }

  return query;
};

export const PageBankAccounts: React.FC = () => {
  const { openAlert } = useDialog();
  const { currentUser, currentPlatform } = useSessionStore();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('accounts');
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { isPinned, togglePin } = usePinnedBankAccounts({ skipFetch: true });
  const { queryParams, setQueryParams } = useQueryParams<BankAccountFilterUrlParams>();
  // Snapshot pagination's starting URL state so the hook owns it after mount.
  const initialPagination = useRef({
    cursor: queryParams?.starting_after ? String(queryParams.starting_after) : undefined,
    pageIndex: Number(queryParams?.page_index) || 0,
  });
  const {
    cursor,
    onNext,
    onPrevious,
    reset: resetPagination,
    setPageSize,
    pageIndex,
    hasPrevious,
  } = usePaginationState({
    defaultPageSize: pageSize,
    initialCursor: initialPagination.current.cursor,
    initialPageIndex: initialPagination.current.pageIndex,
  });

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    updateTableConfig({ limit: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save page size preference' });
    });
  };

  const isBankAccountsSearchEnabled = useFeatureFlag(FeatureFlag.EnableBankAccountsSearch);

  const handleTimeOptionChange = (value: TableTimeFormat | undefined) => {
    if (!value) return;
    updateTableConfig({ timeFormat: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save time format preference' });
    });
  };

  const [filters, setFilters] = useState<BankAccountFilters>(() => filtersFromUrl(queryParams));
  const [queryInput, setQueryInput] = useState(() => filtersFromUrl(queryParams).query);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSmallViewport, setIsSmallViewport] = useState(false);

  // Mirror the filters into the URL so refreshes and shared links preserve the
  // selection. `setQueryParams` replaces the current entry; it's not a new
  // history entry.
  useEffect(() => {
    const base = filtersToUrl(filters);
    setQueryParams(() => ({
      ...base,
      ...(cursor ? { starting_after: cursor } : {}),
      ...(pageIndex > 0 ? { page_index: String(pageIndex) } : {}),
    }));
  }, [filters, cursor, pageIndex, setQueryParams]);

  const updateFilter = <K extends keyof BankAccountFilters>(key: K, value: BankAccountFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const debouncedApplyQuery = useDebounce((value: string) => {
    setFilters((prev) => ({ ...prev, query: value }));
  }, 300);

  const handleQueryInputChange = (value: string) => {
    setQueryInput(value);
    debouncedApplyQuery(value);
  };

  const handleQuerySubmit = (value: string) => {
    if (showEnterHint) {
      navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccounts[0].id}`);
      return;
    }
    setQueryInput(value);
    setFilters((prev) => ({ ...prev, query: value }));
  };

  const handleFilterReset = () => {
    // Preserve the current sort selection; Reset only clears filter-style fields.
    setFilters((prev) => ({ ...EMPTY_FILTERS, sort: prev.sort }));
    setQueryInput('');
  };

  const isBalanceFilterActive = useMemo(
    () =>
      (filters.balance.balanceGte !== undefined && filters.balance.balanceGte > 0) ||
      (filters.balance.balanceLte !== undefined && filters.balance.balanceLte > 0),
    [filters.balance]
  );

  const isDateFilterActive = Boolean(filters.date.createdAtGte || filters.date.createdAtLt);

  const isFilterActive =
    isBalanceFilterActive ||
    isDateFilterActive ||
    Boolean(filters.type) ||
    Boolean(filters.overdraftable) ||
    Boolean(filters.query);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      // Match the transfers overview breakpoints.
      const threshold = isFilterActive ? 1200 : 1000;
      setIsSmallViewport(window.innerWidth < threshold);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFilterActive]);

  const query = useMemo(
    () => buildQuery(filters, isBankAccountsSearchEnabled, pageSize, cursor),
    [filters, isBankAccountsSearchEnabled, pageSize, cursor]
  );

  // Reset pagination whenever the filter set changes so we don't keep
  // paging forward from a cursor that belongs to a different result set.
  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [filters, resetPagination]);

  const {
    data: bankAccountsData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    ...bankAccountListBankAccountsOptions({ query }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const bankAccounts = useMemo<BankAccountRow[]>(() => {
    const entries = bankAccountsData?.bank_accounts ?? [];

    return entries.map((entry) => ({
      ...entry,
      created_at: new Date(entry.created_at),
    }));
  }, [bankAccountsData]);
  const hasMore = bankAccountsData?.has_more ?? false;

  const showEnterHint = !isFetching && filters.query === queryInput && bankAccounts.length === 1 && !hasMore;

  const handleDelete = (entry: BankAccountRow) => {
    openAlert({
      headline: 'Delete bank account',
      content: 'This action cannot be undone.',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        if (!currentUser) {
          return;
        }
        bankAccountDeleteBankAccount({ path: { bank_account_id: entry.id }, throwOnError: true })
          .then(() => {
            addSuccessNotification({
              content:
                entry.display_name || entry.description
                  ? `${entry.display_name || entry.description} deleted`
                  : 'Deleted',
            });
            refetch();
          })
          .catch((error) => {
            addDangerNotification({
              content: error?.message ?? 'Failed to delete bank account',
            });
          });
      },
    });
  };

  const columns: TableColumn<BankAccountRow>[] = [
    {
      id: 'name',
      header: 'Name',
      width: { min: 300, max: '2fr' },
      cell: ({ row }) => (
        <Table.Cell>
          <NamePrimary>{row.display_name || row.description || 'Unnamed'}</NamePrimary>
          {row.display_name && row.description && <NameNote>{row.description}</NameNote>}
          {row.is_overdraftable && (
            <OverdraftableIcon content="Overdraftable">
              <Icon.OverdraftMoney />
            </OverdraftableIcon>
          )}
        </Table.Cell>
      ),
    },
    {
      id: 'balance',
      header: 'Balance',
      width: { min: 'min-content', max: '0.7fr' },
      cell: ({ row }) => (
        <Table.Cell>
          <BalanceAmount>
            <strong>{formatNumber(row.balances?.available_amount ?? 0)}</strong> Available
          </BalanceAmount>
        </Table.Cell>
      ),
    },
    {
      id: 'created_at',
      header: (
        <Dropdown
          size="small"
          positionRight
          active={timeFormat}
          options={[
            { label: 'Relative Time', value: TableTimeFormat.Relative },
            { label: 'Date / Time', value: TableTimeFormat.Absolute },
          ]}
          onChange={handleTimeOptionChange}
          customLabel={
            <Flex alignItems="center" gap={4}>
              Created <Icon.ChevronDown />
            </Flex>
          }
        />
      ),
      width: { fixed: 'min-content' },
      cell: ({ row }) =>
        row.created_at ? (
          <CreatedTimeCell>
            {timeFormat === TableTimeFormat.Relative ? (
              <RelativeTime alignment="right" timestamp={row.created_at} />
            ) : (
              <AbsoluteTime alignment="left" timestamp={row.created_at} />
            )}
          </CreatedTimeCell>
        ) : (
          <Table.Cell />
        ),
    },
  ];

  return (
    <>
      <PageHeader text="Accounts" border={false}>
        <Button
          onClick={() => navigate(ROUTE.COUNTERPARTIES)}
          icon={<Icon.UsersArrow />}
          size="small"
          variant="secondary"
        >
          Counterparties
        </Button>
        <Button onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit`)} icon={<Icon.Plus />} size="small">
          Create Account
        </Button>
      </PageHeader>

      <Toolbar>
        <ToolbarGroup>
          {isSmallViewport ? (
            <>
              <Button size="small" variant="secondary" icon={<Icon.Filter />} onClick={() => setIsFiltersOpen(true)}>
                Filters
              </Button>
              {isFilterActive && (
                <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                  Reset
                </ResetFilter>
              )}
            </>
          ) : (
            <>
              <CompactFilterDropdown
                size="small"
                active={filters.type}
                $isFilterActive={Boolean(filters.type)}
                options={TYPE_OPTIONS}
                label={TYPE_OPTIONS.find((o) => o.value === filters.type)?.label ?? 'Type'}
                onChange={(value: BankAccountType) => updateFilter('type', value)}
              />
              {isBankAccountsSearchEnabled && (
                <PopoverFilterWrapper
                  label="Balance"
                  isActive={isBalanceFilterActive}
                  onSubmit={(data: PopoverFilterBalanceData) => updateFilter('balance', data)}
                >
                  <PopoverFilterBalance data={filters.balance} />
                </PopoverFilterWrapper>
              )}
              <FilterDropdown
                size="small"
                active={filters.overdraftable}
                $isFilterActive={Boolean(filters.overdraftable)}
                options={OVERDRAFTABLE_OPTIONS}
                label={OVERDRAFTABLE_SELECTED_LABELS[filters.overdraftable]}
                onChange={(value: OverdraftableSelection) => updateFilter('overdraftable', value)}
              />
              <PopoverFilterWrapper
                label="Created"
                isActive={isDateFilterActive}
                onSubmit={(data: Partial<PopoverFilterTransferDateData>) => updateFilter('date', data)}
              >
                <PopoverFilterCreatedDate data={filters.date} convertDateRange={convertOptionalDatesToRange} />
              </PopoverFilterWrapper>
              <AccountSearch
                value={queryInput}
                placeholder={isBankAccountsSearchEnabled ? 'Search accounts...' : 'Search by description...'}
                onValueChange={handleQueryInputChange}
                onFilterSubmit={handleQuerySubmit}
                showEnterHint={showEnterHint}
                width={250}
              />
              {isFilterActive && (
                <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                  Reset
                </ResetFilter>
              )}
            </>
          )}
        </ToolbarGroup>
        {isBankAccountsSearchEnabled && (
          <ToolbarGroup>
            <CompactFilterDropdown
              size="small"
              positionRight
              active={filters.sort}
              $isFilterActive={Boolean(filters.sort)}
              options={SORT_OPTIONS}
              label={SORT_OPTIONS.find((o) => o.value === filters.sort)?.label ?? 'Sort'}
              onChange={(value: SortSelection) => updateFilter('sort', value)}
            />
          </ToolbarGroup>
        )}
      </Toolbar>

      <Inner pt={0} px="var(--page-margin)">
        {!isLoading && (bankAccounts?.length ?? 0) === 0 && !isFilterActive ? (
          <EmptyState headline="No accounts found" text="Add your first bank account to get started.">
            <Button onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit`)} size="small" icon={<Icon.Plus />}>
              Create Account
            </Button>
          </EmptyState>
        ) : (
          <Table<BankAccountRow>
            columns={columns}
            data={bankAccounts}
            isLoading={isLoading || isFetching}
            variant={TableVariant.List}
            pagination={{
              hasNext: hasMore,
              hasPrevious,
              pageIndex,
              pageSize,
              onNext: () => {
                if (bankAccounts && bankAccounts.length > 0) {
                  onNext({ cursor: bankAccounts[bankAccounts.length - 1].id });
                }
              },
              onPrevious,
              onPageSizeChange: handlePageSizeChange,
            }}
            onRowClick={(row) => {
              navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${row.id}`);
            }}
            rowAction={(row) => (
              <RowActions>
                <StyledDropdown
                  isInside
                  positionRight
                  options={[
                    {
                      label: 'Edit',
                      onClick: (event: React.SyntheticEvent) => {
                        event.stopPropagation();
                        navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${row.id}`);
                      },
                    },
                    {
                      label: 'Delete',
                      isDanger: true,
                      onClick: (event: React.SyntheticEvent) => {
                        event.stopPropagation();
                        handleDelete(row);
                      },
                    },
                  ]}
                  variant="dots"
                />
                <ButtonPin
                  isPinned={isPinned(row.id)}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    togglePin(row.id, row.display_name || row.description || '');
                  }}
                />
              </RowActions>
            )}
          />
        )}
      </Inner>
      <BankAccountFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        showBalance={isBankAccountsSearchEnabled}
        initialType={filters.type}
        initialOverdraftable={filters.overdraftable}
        initialBalance={filters.balance}
        initialQuery={filters.query}
        initialDate={filters.date}
        convertDateRange={convertOptionalDatesToRange}
        onApply={(values) => {
          setFilters((prev) => ({
            ...prev,
            type: values.type,
            overdraftable: values.overdraftable,
            balance: values.balance,
            query: values.query,
            date: values.date,
          }));
          setQueryInput(values.query);
        }}
      />
    </>
  );
};
